// Przerwa w sezonie (FAZA F, F.11) i rezygnacja (F.25): konto firmy tylko do odczytu. Serwer odrzuca
// wtedy zapisy jedną bramką (Login/backend/lib/przerwaKonta.js) odpowiedzią 403 `account_paused`
// z polem `reason` ('pause' albo 'resignation'; serwer sprzed F.25 go nie wysyła, więc brak = przerwa).
// Tu: rozpoznanie tej odpowiedzi, stan konta z danych sesji i nasłuch na wszystkie żądania panelu
// (fetch i axios), żeby baner i zablokowane przyciski pojawiły się także w karcie otwartej przed
// włączeniem przerwy albo rezygnacji.
// Bez Reacta i bez importu axios (instancję podaje wołający), żeby dało się to sprawdzić testem
// jednostkowym, a moduły sesji mogły go importować bez ładowania axios w testach.
//
// Nazwa `wPrzerwie` zostaje (używają jej ekrany), ale znaczy „konto tylko do odczytu” z dowolnego
// powodu; powód niesie `powod`: 'przerwa' albo 'rezygnacja'.

export const KOD_PRZERWY = 'account_paused';
// Klucz w localStorage dla ekranów bez dostępu do kontekstu (jak isDemo, utils/sessionSync.js).
export const KLUCZ_PRZERWY = 'accountPaused';

const tekst = (v) => (typeof v === 'string' && v ? v : null);

/**
 * Odmowa trybu tylko do odczytu z odpowiedzi serwera: ciało JSON (fetch) albo wyjątek axios.
 * `{ pausedUntil, message, powod, deleteFrom }` albo null.
 */
export function bladPrzerwy(zrodlo) {
  if (!zrodlo || typeof zrodlo !== 'object') return null;
  const dane = zrodlo.response && zrodlo.response.data && typeof zrodlo.response.data === 'object'
    ? zrodlo.response.data
    : zrodlo;
  if (dane.code !== KOD_PRZERWY) return null;
  const rezygnacja = dane.reason === 'resignation';
  return {
    pausedUntil: rezygnacja ? null : tekst(dane.pausedUntil),
    message: typeof dane.error === 'string' ? dane.error : (typeof dane.message === 'string' ? dane.message : ''),
    powod: rezygnacja ? 'rezygnacja' : 'przerwa',
    deleteFrom: rezygnacja ? tekst(dane.deleteFrom) : null,
  };
}

/**
 * Stan konta z obiektu użytkownika (logowanie i /api/whoami). `wPrzerwie`: tryb tylko do odczytu
 * (`accountReadOnly` od F.25, `accountPaused` z serwera sprzed niej); `zaplanowana`: przerwa
 * zaplanowana; `rezygnacjaZaplanowana`: rezygnacja zaplanowana od `resignedAt`.
 */
export function przerwaZUzytkownika(user) {
  const u = user || {};
  const wPrzerwie = u.accountReadOnly === true || u.accountPaused === true;
  const rezygnacja = u.readOnlyReason === 'resignation' || u.accountResigned === true;
  return {
    wPrzerwie,
    powod: wPrzerwie ? (rezygnacja ? 'rezygnacja' : 'przerwa') : null,
    zaplanowana: !wPrzerwie && u.pauseScheduled === true,
    rezygnacjaZaplanowana: !wPrzerwie && u.resignationScheduled === true,
    pausedAt: u.pausedAt || null,
    pausedUntil: tekst(u.pausedUntil),
    resignedAt: u.resignedAt || null,
    dataDeletionFrom: tekst(u.dataDeletionFrom),
  };
}

/** Zapis flagi w localStorage (wołane razem z pozostałymi flagami sesji). */
export function zapiszFlagePrzerwy(storage, user) {
  if (!storage) return;
  try {
    storage.setItem(KLUCZ_PRZERWY, przerwaZUzytkownika(user).wPrzerwie ? '1' : '0');
  } catch (_) { /* pełna albo zablokowana pamięć: flaga to tylko wygoda */ }
}

export function przerwaZPamieci(storage = typeof window !== 'undefined' ? window.localStorage : null) {
  try {
    return !!storage && storage.getItem(KLUCZ_PRZERWY) === '1';
  } catch (_) {
    return false;
  }
}

/**
 * Nasłuch odpowiedzi 403 `account_paused` (przerwa albo rezygnacja) na każdym żądaniu panelu:
 * `fetch` okna i podana instancja axios (domyślna, z niej korzystają wszystkie strony). Zwraca
 * funkcję zdejmującą nasłuch.
 * Ciało czytamy z kopii odpowiedzi, więc strona dostaje odpowiedź nietkniętą.
 */
export function zainstalujNasluchPrzerwy(onPrzerwa, { win = typeof window !== 'undefined' ? window : null, ax = null } = {}) {
  const sprzatanie = [];

  if (win && typeof win.fetch === 'function') {
    const oryginalny = win.fetch;
    // `fetch` wymaga okna jako `this` („Illegal invocation” przy wywołaniu bez obiektu).
    const opakowany = (...args) => oryginalny.apply(win, args).then((res) => {
      if (res && res.status === 403 && typeof res.clone === 'function') {
        res.clone().json().then((dane) => {
          const odmowa = bladPrzerwy(dane);
          if (odmowa) onPrzerwa(odmowa);
        }).catch(() => {});
      }
      return res;
    });
    win.fetch = opakowany;
    sprzatanie.push(() => { if (win.fetch === opakowany) win.fetch = oryginalny; });
  }

  if (ax && ax.interceptors && ax.interceptors.response) {
    const id = ax.interceptors.response.use(
      (odpowiedz) => odpowiedz,
      (err) => {
        const odmowa = err && err.response && err.response.status === 403 ? bladPrzerwy(err) : null;
        if (odmowa) onPrzerwa(odmowa);
        return Promise.reject(err);
      }
    );
    sprzatanie.push(() => ax.interceptors.response.eject(id));
  }

  return () => sprzatanie.forEach((f) => f());
}
